/**
 * The composer's + menu. Upload a file, a photo (on a phone, `image/*` offers
 * the camera), start an image prompt, or turn Research on or off.
 *
 * "From Cloud" is in the prototype and hidden here until the Cloud picker
 * exists; the upload rows go when the deployment has no file storage, and the
 * image row when it cannot make images. With nothing left, there is no +.
 */

import { useRef, type ChangeEvent } from 'react';
import { Menu, MenuItem, Ph, useDismiss } from '../ui';

export function AttachMenu({
  open,
  onToggle,
  onClose,
  canUpload,
  canImage,
  onFiles,
  onImagePrompt,
  canResearch = false,
  research = false,
  onResearch
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  canUpload: boolean;
  canImage: boolean;
  onFiles: (files: File[]) => void;
  onImagePrompt: () => void;
  canResearch?: boolean;
  research?: boolean;
  onResearch?: () => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  useDismiss(ref, open, onClose);

  if (!canUpload && !canImage && !canResearch) return null;

  const picked = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Cleared so picking the same file again still fires `change`.
    e.target.value = '';
    if (files.length) onFiles(files);
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'flex', flex: 'none' }}>
      <button
        type="button"
        className="bare hov-soft"
        aria-label="Add"
        title="Add"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
        style={{
          width: 36,
          height: 36,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text-muted)'
        }}
      >
        <Ph name="plus" size={18} />
      </button>

      <input ref={fileInput} type="file" multiple hidden onChange={picked} />
      <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={picked} />

      {open ? (
        <Menu style={{ left: 0, bottom: 'calc(100% + 6px)', width: 220 }}>
          {canUpload ? (
            <>
              <MenuItem
                icon="paperclip"
                label="Upload a file"
                onSelect={() => {
                  onClose();
                  fileInput.current?.click();
                }}
                style={{ color: 'var(--text-body)' }}
              />
              <MenuItem
                icon="image"
                label="Photo"
                onSelect={() => {
                  onClose();
                  photoInput.current?.click();
                }}
                style={{ color: 'var(--text-body)' }}
              />
            </>
          ) : null}
          {canImage ? (
            <MenuItem
              icon="magic-wand"
              label="Create an image"
              onSelect={() => {
                onClose();
                onImagePrompt();
              }}
              style={{ color: 'var(--text-body)' }}
            />
          ) : null}
          {canResearch && onResearch ? (
            <MenuItem
              icon="magnifying-glass"
              label="Research"
              selected={research}
              onSelect={() => {
                onClose();
                onResearch();
              }}
              style={{ color: 'var(--text-body)' }}
            />
          ) : null}
        </Menu>
      ) : null}
    </span>
  );
}
